import { useState, useEffect, useMemo, useCallback } from 'react';
import { useApp } from '../../context';
import api from '../../hooks/useApi';
import { formatDate } from '../../utils/format';
import { useToast } from '../ui/Toast';
import Modal from '../shared/Modal';
import useRefreshOnFocus from '../../hooks/useRefreshOnFocus';
import Avatar from '../shared/Avatar';

// ── Status config (covers both generic tasks and SOW project tasks) ──────────
const STATUS_LABELS = {
  todo: 'To Do', not_started: 'Not Started', in_progress: 'In Progress',
  on_hold: 'On Hold', blocked: 'Blocked', done: 'Done', completed: 'Completed',
  cancelled: 'Cancelled', n_a: 'N/A',
};
function statusColor(s) {
  if (s === 'in_progress') return 'bg-blue-100 text-blue-700';
  if (s === 'done' || s === 'completed') return 'bg-green-100 text-green-700';
  if (s === 'on_hold') return 'bg-amber-100 text-amber-700';
  if (s === 'blocked') return 'bg-red-100 text-red-700';
  if (s === 'cancelled' || s === 'n_a') return 'bg-gray-100 text-gray-500';
  return 'bg-gray-100 text-gray-600';
}
const STATUS_OPTIONS = {
  task:     ['todo', 'in_progress', 'on_hold', 'blocked', 'done', 'cancelled'],
  sow_task: ['not_started', 'in_progress', 'blocked', 'completed', 'n_a'],
};

function initials(name) {
  if (!name) return '?';
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase();
}

// ── Slip badge: how many times the due date has been pushed to the right ──────
function SlipBadge({ count }) {
  if (!count) return null;
  const sev = count >= 3 ? 'bg-red-100 text-red-700' : count >= 2 ? 'bg-orange-100 text-orange-700' : 'bg-amber-100 text-amber-700';
  return (
    <span className={`text-[7px] font-bold px-1 py-0.5 rounded ${sev}`} title={`Due date moved later ${count} time${count > 1 ? 's' : ''}`}>
      ⚠ moved {count}×
    </span>
  );
}

function OverdueBadge({ days }) {
  if (!days || days <= 0) return null;
  return (
    <span className="text-[7px] bg-red-100 text-red-600 font-bold px-1 py-0.5 rounded" title={`${days} day${days > 1 ? 's' : ''} overdue`}>
      {days}d overdue
    </span>
  );
}

// ── KPI card ──────────────────────────────────────────────────────────────────
function Kpi({ label, value, color, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`bg-white border rounded p-2 text-left transition-all ${active ? 'border-navy ring-1 ring-navy/20' : 'border-gray-200 hover:border-gray-300'}`}
    >
      <div className="text-[8px] text-gray-500 uppercase font-bold">{label}</div>
      <div className={`text-base font-extrabold ${color}`}>{value}</div>
    </button>
  );
}

export default function TaskOversightPage() {
  const { currentUser } = useApp();
  const { toast } = useToast();
  const [tasks, setTasks]       = useState([]);
  const [people, setPeople]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState({}); // key -> bool
  const [detail, setDetail]     = useState(null); // task in detail modal

  // Filters
  const [search, setSearch]         = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [problemsOnly, setProblemsOnly] = useState(false);
  const [groupBy, setGroupBy]       = useState('person'); // 'person' | 'team'

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('tasks/overview'),
      api.get('people'),
    ]).then(([t, p]) => {
      setTasks(Array.isArray(t) ? t : []);
      setPeople(Array.isArray(p) ? p : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);
  useEffect(load, [load]);
  useRefreshOnFocus(load);

  // Distinct teams present in the data
  const teams = useMemo(() => {
    const m = new Map();
    tasks.forEach(t => { if (t.team_id) m.set(t.team_id, t.team_name || 'Team'); });
    return [...m.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [tasks]);

  const isProblem = useCallback((t) => t.at_risk || t.is_overdue || !t.owner_id, []);

  // Apply filters
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tasks.filter(t => {
      if (q && !(`${t.name} ${t.owner_name || ''} ${t.sow_reference || ''} ${t.project_name || ''}`.toLowerCase().includes(q))) return false;
      if (teamFilter && t.team_id !== teamFilter) return false;
      if (statusFilter && t.status !== statusFilter) return false;
      if (sourceFilter && t.task_source !== sourceFilter) return false;
      if (problemsOnly && !isProblem(t)) return false;
      return true;
    });
  }, [tasks, search, teamFilter, statusFilter, sourceFilter, problemsOnly, isProblem]);

  // Top-line stats (from filtered set so cards reflect current scope)
  const stats = useMemo(() => ({
    total:      filtered.length,
    overdue:    filtered.filter(t => t.is_overdue).length,
    atRisk:     filtered.filter(t => t.at_risk).length,
    blocked:    filtered.filter(t => t.status === 'blocked' || t.status === 'on_hold').length,
    unassigned: filtered.filter(t => !t.owner_id).length,
  }), [filtered]);

  // Group tasks by person (default) or by team
  const groups = useMemo(() => {
    const byKey = new Map();
    for (const t of filtered) {
      let key, label, sub;
      if (groupBy === 'team') {
        key = t.team_id || '__noteam__';
        label = t.team_name || 'No team';
        sub = null;
      } else {
        key = t.owner_id || '__unassigned__';
        label = t.owner_name || 'Unassigned';
        sub = t.team_name || null;
      }
      if (!byKey.has(key)) byKey.set(key, { key, label, sub, tasks: [], owner_status: t.owner_status });
      byKey.get(key).tasks.push(t);
    }
    const arr = [...byKey.values()].map(g => ({
      ...g,
      overdue: g.tasks.filter(t => t.is_overdue).length,
      atRisk:  g.tasks.filter(t => t.at_risk).length,
      problems: g.tasks.filter(isProblem).length,
    }));
    // Sort: most problems first, then most tasks, unassigned pinned near top
    arr.sort((a, b) => {
      if (a.key === '__unassigned__') return -1;
      if (b.key === '__unassigned__') return 1;
      if (b.problems !== a.problems) return b.problems - a.problems;
      return b.tasks.length - a.tasks.length;
    });
    return arr;
  }, [filtered, groupBy, isProblem]);

  const toggle = (key) => setExpanded(p => ({ ...p, [key]: !p[key] }));

  // ── Inline task mutations (reused from dashboard patterns) ──────────────────
  const patchTask = async (task, patch, journey) => {
    try {
      if (task.task_source === 'sow_task') {
        const sowPatch = { ...patch };
        if ('end_date' in sowPatch) { sowPatch.start_date = sowPatch.end_date; delete sowPatch.end_date; }
        if ('assignee_id' in sowPatch) { sowPatch.owner_id = sowPatch.assignee_id; delete sowPatch.assignee_id; }
        await api.put(`sows/${task.sow_id}/project-tasks/${task.id}`, sowPatch);
      } else {
        await api.put(`tasks/${task.id}`, patch);
      }
      if (journey) {
        await api.post('commentary', { entity_type: 'task', entity_id: task.id, content: journey }).catch(() => {});
      }
      // Optimistic local update
      const merged = { ...patch };
      if ('assignee_id' in patch) {
        const person = people.find(p => p.id === patch.assignee_id);
        merged.owner_id = patch.assignee_id;
        merged.owner_name = person ? `${person.first_name} ${person.last_name}` : null;
        merged.team_id = person?.team_id ?? task.team_id;
      }
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, ...merged } : t));
      setDetail(d => d && d.id === task.id ? { ...d, ...merged } : d);
      return true;
    } catch (e) {
      toast.error('Update failed: ' + e.message);
      return false;
    }
  };

  const actorName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}`.trim() : 'A manager';

  if (loading) return <div className="p-6 text-[10px] text-gray-400">Loading task oversight…</div>;

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-extrabold text-navy">Task Oversight</h2>
          <div className="text-[9px] text-gray-500">Every live task across the team — who owns it, where it sits, and which ones are slipping. Tasks drive delivery; nothing should quietly drift to the right.</div>
        </div>
        <button onClick={load} className="px-3 py-1.5 border border-gray-200 text-[10px] font-semibold rounded hover:bg-gray-50">↻ Refresh</button>
      </div>

      {/* KPI cards (click to filter) */}
      <div className="grid grid-cols-5 gap-2 mb-3">
        <Kpi label="Active Tasks" value={stats.total} color="text-navy" active={!problemsOnly && !statusFilter} onClick={() => { setProblemsOnly(false); setStatusFilter(''); }} />
        <Kpi label="Overdue" value={stats.overdue} color="text-red-500" active={false} onClick={() => { setProblemsOnly(true); setStatusFilter(''); }} />
        <Kpi label="At Risk" value={stats.atRisk} color="text-orange-500" active={problemsOnly} onClick={() => setProblemsOnly(v => !v)} />
        <Kpi label="Blocked / Held" value={stats.blocked} color="text-amber-500" active={statusFilter === 'blocked'} onClick={() => setStatusFilter(statusFilter === 'blocked' ? '' : 'blocked')} />
        <Kpi label="Unassigned" value={stats.unassigned} color="text-purple-500" active={false} onClick={() => { setGroupBy('person'); setProblemsOnly(false); setExpanded(p => ({ ...p, __unassigned__: true })); }} />
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 items-center mb-3">
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search task, person, SOW…"
          className="text-[10px] border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:border-navy w-56"
        />
        <select value={teamFilter} onChange={e => setTeamFilter(e.target.value)} className="text-[10px] border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:border-navy">
          <option value="">All teams</option>
          {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="text-[10px] border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:border-navy">
          <option value="">All statuses</option>
          {['todo', 'not_started', 'in_progress', 'on_hold', 'blocked'].map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
        <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} className="text-[10px] border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:border-navy">
          <option value="">All types</option>
          <option value="task">Standalone tasks</option>
          <option value="sow_task">SOW tasks</option>
        </select>
        <label className="flex items-center gap-1 text-[9px] text-gray-600 cursor-pointer">
          <input type="checkbox" checked={problemsOnly} onChange={e => setProblemsOnly(e.target.checked)} /> Problems only
        </label>
        <div className="ml-auto flex items-center gap-1 text-[9px]">
          <span className="text-gray-400">Group by</span>
          <div className="flex border border-gray-200 rounded overflow-hidden">
            <button onClick={() => setGroupBy('person')} className={`px-2 py-1 font-semibold ${groupBy === 'person' ? 'bg-navy text-white' : 'bg-white text-gray-600'}`}>Person</button>
            <button onClick={() => setGroupBy('team')} className={`px-2 py-1 font-semibold ${groupBy === 'team' ? 'bg-navy text-white' : 'bg-white text-gray-600'}`}>Team</button>
          </div>
        </div>
      </div>

      {/* Groups */}
      {groups.length === 0 ? (
        <div className="text-center text-[10px] text-gray-400 py-10 border border-dashed border-gray-200 rounded">No tasks match these filters.</div>
      ) : (
        <div className="space-y-1.5">
          {groups.map(g => {
            const isOpen = !!expanded[g.key];
            const isUnassigned = g.key === '__unassigned__';
            const archived = g.owner_status && ['left', 'archived'].includes(g.owner_status);
            return (
              <div key={g.key} className="border border-gray-200 rounded bg-white">
                {/* Group header row */}
                <button onClick={() => toggle(g.key)} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 text-left">
                  <span className="text-gray-300 text-[10px] w-3">{isOpen ? '▾' : '▸'}</span>
                  {groupBy === 'person' && (
                    <Avatar as="span" personId={isUnassigned ? null : g.key} className={`w-6 h-6 rounded-full flex items-center justify-center text-[8px] font-bold text-white shrink-0 ${isUnassigned ? 'bg-purple-400' : 'bg-navy'}`}>
                      {isUnassigned ? '∅' : initials(g.label)}
                    </Avatar>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-bold text-gray-800 truncate">{g.label}</span>
                      {groupBy === 'person' && g.sub && <span className="text-[8px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-semibold">{g.sub}</span>}
                      {archived && <span className="text-[7px] bg-gray-200 text-gray-500 px-1 py-0.5 rounded font-bold uppercase">Archived</span>}
                    </div>
                  </div>
                  {/* Count chips */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-[8px] bg-gray-100 text-gray-600 font-bold px-1.5 py-0.5 rounded">{g.tasks.length} task{g.tasks.length !== 1 ? 's' : ''}</span>
                    {g.overdue > 0 && <span className="text-[8px] bg-red-100 text-red-600 font-bold px-1.5 py-0.5 rounded">{g.overdue} overdue</span>}
                    {g.atRisk > 0 && <span className="text-[8px] bg-orange-100 text-orange-600 font-bold px-1.5 py-0.5 rounded">{g.atRisk} at risk</span>}
                  </div>
                </button>

                {/* Task rows */}
                {isOpen && (
                  <div className="border-t border-gray-100 divide-y divide-gray-50">
                    {g.tasks
                      .slice()
                      .sort((a, b) => {
                        if (a.is_overdue !== b.is_overdue) return a.is_overdue ? -1 : 1;
                        if (!a.end_date) return 1;
                        if (!b.end_date) return -1;
                        return a.end_date < b.end_date ? -1 : 1;
                      })
                      .map(t => (
                        <div
                          key={t.id}
                          onClick={() => setDetail(t)}
                          className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-gray-50 ${t.is_overdue ? 'border-l-2 border-l-red-400' : t.at_risk ? 'border-l-2 border-l-orange-300' : ''}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-[10px] font-semibold text-gray-800 truncate">{t.name}</span>
                              {t.sow_reference && <span className="text-[8px] bg-navy/10 text-navy font-semibold px-1 rounded">{t.sow_reference}</span>}
                              {!t.sow_reference && t.project_name && <span className="text-[8px] text-gray-400 truncate">{t.project_name}</span>}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              {groupBy === 'team' && t.owner_name && <span className="text-[8px] text-gray-400 italic">👤 {t.owner_name}</span>}
                              {groupBy === 'team' && !t.owner_name && <span className="text-[8px] text-purple-400 italic">unassigned</span>}
                              {t.end_date
                                ? <span className="text-[8px] text-gray-400">due {formatDate(t.end_date)}</span>
                                : <span className="text-[8px] text-gray-300 italic">no due date</span>}
                              <OverdueBadge days={t.days_overdue} />
                              <SlipBadge count={t.slip_count} />
                            </div>
                          </div>
                          <span className={`shrink-0 text-[7px] font-bold px-1.5 py-0.5 rounded uppercase ${statusColor(t.status)}`}>
                            {STATUS_LABELS[t.status] || t.status}
                          </span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {detail && (
        <TaskDetailModal
          task={detail}
          people={people}
          actorName={actorName}
          onClose={() => setDetail(null)}
          onPatch={patchTask}
        />
      )}
    </div>
  );
}

// ── Detail modal: full context + inline drive-forward controls ───────────────
function TaskDetailModal({ task, people, actorName, onClose, onPatch }) {
  const [saving, setSaving] = useState(false);
  const [dueDate, setDueDate] = useState(task.end_date || '');
  const statusOpts = STATUS_OPTIONS[task.task_source] || STATUS_OPTIONS.task;
  const activePeople = people.filter(p => !p.employee_status || !['archived', 'left'].includes(p.employee_status));

  const doStatus = async (newStatus) => {
    if (newStatus === task.status) return;
    setSaving(true);
    await onPatch(task, { status: newStatus },
      `📋 Status changed ${STATUS_LABELS[task.status] || task.status} → ${STATUS_LABELS[newStatus] || newStatus} by ${actorName}.`);
    setSaving(false);
  };
  const doOwner = async (newOwnerId) => {
    setSaving(true);
    const person = people.find(p => p.id === newOwnerId);
    const name = person ? `${person.first_name} ${person.last_name}` : 'Unassigned';
    await onPatch(task, { assignee_id: newOwnerId || null }, `👤 Reassigned to ${name} by ${actorName}.`);
    setSaving(false);
  };
  const doDueDate = async () => {
    setSaving(true);
    const moved = task.end_date && dueDate && dueDate > task.end_date;
    await onPatch(task, { end_date: dueDate || null },
      dueDate
        ? `📅 Due date ${moved ? 'pushed to' : 'set to'} ${dueDate} by ${actorName}${moved ? ' (moved later).' : '.'}`
        : `📅 Due date cleared by ${actorName}.`);
    setSaving(false);
  };

  return (
    <Modal title={task.name} onClose={onClose} wide footer={
      <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Close</button>
    }>
      <div className="space-y-3">
        {/* Context strip */}
        <div className="flex flex-wrap items-center gap-2">
          <span className={`text-[8px] font-bold px-2 py-0.5 rounded uppercase ${statusColor(task.status)}`}>{STATUS_LABELS[task.status] || task.status}</span>
          {task.sow_reference && <span className="text-[8px] bg-navy/10 text-navy font-semibold px-1.5 py-0.5 rounded">{task.sow_reference}</span>}
          {task.task_source === 'sow_task' ? <span className="text-[8px] text-gray-400">SOW task</span> : <span className="text-[8px] text-gray-400">Standalone task</span>}
          {task.is_overdue && <OverdueBadge days={task.days_overdue} />}
          <SlipBadge count={task.slip_count} />
        </div>

        {task.description && <div className="text-[10px] text-gray-600 bg-gray-50 border border-gray-100 rounded p-2 whitespace-pre-wrap">{task.description}</div>}

        {/* Slip summary */}
        {(task.slip_count > 0 || task.original_due) && (
          <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[9px] text-amber-800">
            {task.slip_count > 0
              ? <>This due date has been pushed later <strong>{task.slip_count}</strong> time{task.slip_count > 1 ? 's' : ''}.</>
              : <>Due date history on record.</>}
            {task.original_due && <> Originally due <strong>{formatDate(task.original_due)}</strong>{task.end_date && <> · now <strong>{formatDate(task.end_date)}</strong></>}.</>}
          </div>
        )}

        {/* Drive-forward controls */}
        <div className="grid grid-cols-3 gap-3 border-t border-gray-100 pt-3">
          <div>
            <label className="block text-[7.5px] font-bold text-gray-500 uppercase mb-0.5">Status</label>
            <select disabled={saving} value={task.status} onChange={e => doStatus(e.target.value)} className="form-select text-[9px]">
              {statusOpts.map(s => <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[7.5px] font-bold text-gray-500 uppercase mb-0.5">Owner</label>
            <select disabled={saving} value={task.owner_id || ''} onChange={e => doOwner(e.target.value)} className="form-select text-[9px]">
              <option value="">— Unassigned —</option>
              {activePeople.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[7.5px] font-bold text-gray-500 uppercase mb-0.5">Due date</label>
            <div className="flex gap-1">
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className="form-input text-[9px]" />
              <button disabled={saving || dueDate === (task.end_date || '')} onClick={doDueDate} className="px-2 py-1 text-[9px] font-semibold bg-navy text-white rounded disabled:opacity-40 whitespace-nowrap">Set</button>
            </div>
          </div>
        </div>
        <div className="text-[8px] text-gray-400">Status, owner and due-date changes are logged to the task's activity history.</div>
      </div>
    </Modal>
  );
}
